/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **What a template will carry, rendered** — the one drawing of `TM5`'s disclosure (`#1171`).
 *
 * `template-disclosure.ts` holds the decision (which planes, which literals, in what order) and this
 * holds the drawing, which is the split that file already documents. What it did **not** hold was
 * the markup: the wizard's phase 4 and `CloneArtifactDialog` each wrote their own copy of the same
 * list — label, count, the first few `path = value` lines, *…and N more stored values* — and `ST12`
 * item 2 was about to add a third, on the *Create template* confirm.
 *
 * Three identical renderings of one claim is the shape `#1229` names a control at a time: every copy
 * is correct, so nothing can detect them, and they drift the day one of them gains a line. There is
 * one now, and the surfaces differ only in what they say *around* it.
 */

import React from 'react';
import { Box, Text, VStack } from '@chakra-ui/react';

import { TEMPLATE_DISCLOSURE_LITERAL_LIMIT, type TemplateDisclosureLine } from './template-disclosure';

export interface TemplateDisclosureListProps {
  lines: readonly TemplateDisclosureLine[];
  /**
   * What to say when the template would carry nothing.
   *
   * Required and nullable (`#1134`): `null` is a surface that renders the list only when it has
   * lines and so never meets the empty case, which is a different statement from *say nothing*.
   */
  emptyNote: string | null;
}

export const TemplateDisclosureList: React.FC<TemplateDisclosureListProps> = ({ lines, emptyNote }) => {
  if (lines.length === 0) {
    return emptyNote === null ? null : (
      <Text fontSize="xs" color="fg.subtle">
        {emptyNote}
      </Text>
    );
  }

  return (
    <VStack align="stretch" gap={2}>
      {lines.map((line) => (
        <Box key={line.label}>
          <Text fontSize="xs" fontWeight="600" color="fg.default">
            {line.label}{' '}
            <Text as="span" fontWeight="400" color="fg.muted">
              ({line.count})
            </Text>
          </Text>
          {/* Paths and values, not a count — *the operator can only remove what they are told is
              there*, which is the whole argument in `template-disclosure.ts`. */}
          {line.literals.slice(0, TEMPLATE_DISCLOSURE_LITERAL_LIMIT).map((literal) => (
            <Text key={literal.path} fontSize="2xs" color="fg.muted" fontFamily="mono" truncate>
              {literal.path} = {literal.value}
            </Text>
          ))}
          {line.literals.length > TEMPLATE_DISCLOSURE_LITERAL_LIMIT && (
            <Text fontSize="2xs" color="fg.subtle">
              …and {line.literals.length - TEMPLATE_DISCLOSURE_LITERAL_LIMIT} more stored values
            </Text>
          )}
        </Box>
      ))}
    </VStack>
  );
};
